import ShelfPolls from '../components/polls/polls';
import ShelfLogin from '../components/login/login';
import ShelfForgotPassword from '../components/forgot-password/forgotPassword';
import Landing from '../components/landing/Landing';
import ShelfUsers from '../components/users/users';
import ShelfPrices from '../components/price/price';
import ShelfStores from '../components/stores/stores';
import ShelfClients from '../components/clients/clients';
import ShelfProducts from '../components/products/products';
import ShelfPriceList from '../components/price/list/list';
import ShelfAuditors from '../components/auditors/auditors';
import ShelfSegments from '../components/segments/segments';
import ShelfDashboard from '../components/dashboard/dashboard';
import ShelfPromoters from '../components/promotors/promotors';
import ShelfStoreProfile from '../components/stores/store/store';
import ShelfClientProfile from '../components/clients/client/client';
import ShelfSegmentList from '../components/segments/segment/segment';
import ShelfAuditorProfile from '../components/auditors/auditor/auditor';
import ShelfAuditorFieldOptions from '../components/auditor-field-options/auditorFieldOptions';
import ShelfAuditConsole from '../components/audit-console/auditConsole';
import ShelfProductApproval from '../components/product-approval/productApproval';
import ShelfStoreCheckProfile from '../components/storecheck/storecheck';
import ShelfPromoterProfile from '../components/promotors/promoter/promoter';
import PromotorAnalytics from '../components/promotors/analytics/analytics';
import ShelfCampaigns from '../components/campaigns/campaigns';
import ShelfCampaign from '../components/campaigns/campaign/campaign';
import ShelfContracts from '../components/contracts/contracts';
import ShelfContract from '../components/contracts/contract/contract';
import ShelfNotifications from '../components/notifications/notifications';
import ShelfTags from '../components/tags/tags';
import ShelfSOSAnalysis from '../components/sos-analysis/sos-analysis';
import ShelfActivityLogs from '../components/activity-logs/activityLogs';
import ShelfUserInteractions from '../components/user-interactions/userInteractions';
import ShelfRouteEfficiency from '../components/route-efficiency/routeEfficiency';
import ShelfDistributorPrices from '../components/distributor-prices/distributorPrices';
import ShelfLevantamientoGeneral from '../components/levantamiento-general/levantamientoGeneral';
import ShelfPermissions from '../components/permissions/permissions';
import ShelfLevantamientoCharts from '../components/levantamiento-general/levantamientoCharts';
import ShelfUsoDeShelf from '../components/uso-de-shelf/usoDeShelf';
import ShelfShareOfShelf from '../components/share-of-shelf/shareOfShelf';
import ShelfNumericDistribution from '../components/numeric-distribution/numericDistribution';
import Support from '../components/support/Support';
import PrivacyPolicy from '../components/privacy/PrivacyPolicy';

import MaryRegistration from '../companies/mary';
import MimesaRegistration from '../companies/mimesa';
import { BASE_ROUTES } from './routeTable';
import { DEFAULT_COMPANY_ID } from './companies';

/**
 * Page registry, after the clockout-packages screen registry: the base page of every route
 * key, plus each company's registration (`companies/<id>/index.js`):
 *
 *   { pages: { Key: CompanyPage }, extraRoutes: [{ key, path, screen, guard?, component }], remove: [key] }
 *
 * `resolvePage(key, companyId)` returns the company's page when it registers one, otherwise the
 * base page. `resolveRoutes(companyId)` is the route table the router mounts:
 * (base routes − `remove`) with pages resolved, plus `extraRoutes`. Everything here is built
 * at module scope, once per company, never per render.
 */

export const BASE_PAGES = Object.freeze({
    Landing,
    Login: ShelfLogin,
    ForgotPassword: ShelfForgotPassword,
    Support,
    PrivacyPolicy,
    Dashboard: ShelfDashboard,
    Notifications: ShelfNotifications,
    Clients: ShelfClients,
    ClientProfile: ShelfClientProfile,
    Stores: ShelfStores,
    StoreProfile: ShelfStoreProfile,
    StoreCheck: ShelfStoreCheckProfile,
    Users: ShelfUsers,
    Permissions: ShelfPermissions,
    Prices: ShelfPrices,
    Products: ShelfProducts,
    PriceList: ShelfPriceList,
    Segments: ShelfSegments,
    Segment: ShelfSegmentList,
    Promoters: ShelfPromoters,
    PromotersAnalytics: PromotorAnalytics,
    PromoterProfile: ShelfPromoterProfile,
    Polls: ShelfPolls,
    Auditors: ShelfAuditors,
    AuditorFieldOptions: ShelfAuditorFieldOptions,
    AuditConsole: ShelfAuditConsole,
    ProductApproval: ShelfProductApproval,
    AuditorProfile: ShelfAuditorProfile,
    Contracts: ShelfContracts,
    Contract: ShelfContract,
    Campaigns: ShelfCampaigns,
    Campaign: ShelfCampaign,
    Tags: ShelfTags,
    SOSAnalysis: ShelfSOSAnalysis,
    ActivityLogs: ShelfActivityLogs,
    UserInteractions: ShelfUserInteractions,
    RouteEfficiency: ShelfRouteEfficiency,
    DistributorPrices: ShelfDistributorPrices,
    LevantamientoGeneral: ShelfLevantamientoGeneral,
    LevantamientoCharts: ShelfLevantamientoCharts,
    UsoDeShelf: ShelfUsoDeShelf,
    ShareOfShelf: ShelfShareOfShelf,
    NumericDistribution: ShelfNumericDistribution,
});

export const REGISTRATIONS = Object.freeze({
    [MaryRegistration.id]: MaryRegistration,
    [MimesaRegistration.id]: MimesaRegistration,
});

function registrationFor(companyId) {
    return REGISTRATIONS[companyId] || REGISTRATIONS[DEFAULT_COMPANY_ID];
}

/**
 * The page component to mount for `key`: the company's override, else the base page.
 * @param {string} key - A key of BASE_PAGES
 * @param {string} companyId
 */
export function resolvePage(key, companyId) {
    const pages = registrationFor(companyId).pages || {};
    return Object.prototype.hasOwnProperty.call(pages, key) ? pages[key] : BASE_PAGES[key];
}

function buildRoutes(companyId) {
    const registration = registrationFor(companyId);
    const removed = new Set(registration.remove || []);
    const mounted = BASE_ROUTES.filter((route) => !removed.has(route.key)).map((route) =>
        Object.freeze({ ...route, component: resolvePage(route.key, companyId) })
    );
    const taken = new Set(mounted.map((route) => route.path));
    const extras = (registration.extraRoutes || []).filter((route) => {
        if (taken.has(route.path)) {
            console.warn(`Extra route ${route.key} (${route.path}) collides with a mounted route and is ignored; replace that page through \`pages\`.`);
            return false;
        }
        return true;
    });
    const removedRoutes = BASE_ROUTES.filter((route) => removed.has(route.key));
    return Object.freeze({ routes: Object.freeze([...mounted, ...extras]), removed: Object.freeze(removedRoutes) });
}

const ROUTES_BY_COMPANY = Object.freeze(
    Object.fromEntries(Object.keys(REGISTRATIONS).map((companyId) => [companyId, buildRoutes(companyId)]))
);

/**
 * `{ routes, removed }` for `companyId`: the routes to mount (base minus `remove`, pages
 * resolved, plus `extraRoutes`) and the base routes the company dropped (the router sends
 * their URLs home).
 */
export function resolveRoutes(companyId) {
    return ROUTES_BY_COMPANY[companyId] || ROUTES_BY_COMPANY[DEFAULT_COMPANY_ID];
}
